/**
 * Centralized Activity Type Definitions
 * Ensures consistent icons, colors, and labels across all activity displays
 */

import { 
  Package, 
  Truck, 
  CheckCircle2, 
  ClipboardList, 
  Edit3, 
  Trash2, 
  StickyNote, 
  ArrowRightLeft,
  Wrench,
  Undo2,
  AlertTriangle,
  Archive,
  Circle
} from 'lucide-react';

// Activity type definitions with consistent colors and icons
export const ACTIVITY_TYPES = {
  // Equipment Planning Activities
  'EQUIPMENT_PLANNED': {
    label: 'Planned',
    icon: ClipboardList,
    color: '#4caf50', // Green
    chipColor: 'success'
  },
  'EQUIPMENT_PLAN_UPDATED': {
    label: 'Plan Updated',
    icon: Edit3,
    color: '#2196f3', // Blue
    chipColor: 'primary'
  },
  'EQUIPMENT_PLAN_DELETED': {
    label: 'Plan Deleted',
    icon: Trash2,
    color: '#f44336', // Red
    chipColor: 'error'
  },
  
  // Equipment Lifecycle Activities
  'EQUIPMENT_ORDERED': {
    label: 'Ordered',
    icon: Package,
    color: '#9c27b0', // Purple (consistent with ordered status)
    chipColor: 'secondary'
  },
  'EQUIPMENT_RECEIVED': {
    label: 'Received',
    icon: CheckCircle2,
    color: '#4caf50', // Green
    chipColor: 'success'
  },
  'EQUIPMENT_ASSIGNED': {
    label: 'Assigned',
    icon: ClipboardList,
    color: '#ff9800', // Orange
    chipColor: 'warning'
  },
  'EQUIPMENT_CHECKED_OUT': {
    label: 'Checked Out',
    icon: Truck,
    color: '#9c27b0', // Purple (matches checked_out status)
    chipColor: 'secondary'
  },
  'EQUIPMENT_INSTALLED': {
    label: 'Installed',
    icon: Wrench,
    color: '#2196f3', // Blue (matches installed status)
    chipColor: 'primary'
  },
  'EQUIPMENT_TRANSFERRED': {
    label: 'Transferred',
    icon: ArrowRightLeft,
    color: '#00bcd4', // Cyan
    chipColor: 'info'
  },
  'EQUIPMENT_RETURNED': {
    label: 'Returned',
    icon: Undo2,
    color: '#4caf50', // Green (matches returned status)
    chipColor: 'success'
  },
  'EQUIPMENT_RETURNED_TO_JOB': {
    label: 'Returned to Job',
    icon: Undo2,
    color: '#4caf50', // Green (matches returned status)
    chipColor: 'success'
  },

  // Maintenance Activities
  'EQUIPMENT_MAINTENANCE': {
    label: 'Maintenance',
    icon: AlertTriangle,
    color: '#f44336', // Red
    chipColor: 'error'
  },
  'EQUIPMENT_ARCHIVED': {
    label: 'Archived',
    icon: Archive,
    color: '#607d8b', // Grey
    chipColor: 'default'
  },
  
  // General Activities
  'NOTE_ADDED': {
    label: 'Note Added',
    icon: StickyNote,
    color: '#607d8b', // Grey
    chipColor: 'default'
  },
  'STATUS_CHANGED': {
    label: 'Status Changed',
    icon: Circle,
    color: '#9e9e9e', // Light Grey
    chipColor: 'default'
  }
};

// Get activity display properties
export const getActivityDisplay = (actionType) => {
  const config = ACTIVITY_TYPES[actionType];
  
  if (!config) {
    return {
      label: actionType ? actionType.replace(/_/g, ' ').toLowerCase() : 'Unknown',
      icon: Circle,
      color: '#9e9e9e',
      chipColor: 'default'
    };
  }
  
  return config;
};

// Get formatted label for activity type
export const getActivityLabel = (actionType) => {
  const display = getActivityDisplay(actionType);
  return display.label;
};

// Get icon component for activity type
export const getActivityIcon = (actionType) => {
  const display = getActivityDisplay(actionType);
  return display.icon;
};

// Get color for activity type
export const getActivityColor = (actionType) => {
  const display = getActivityDisplay(actionType);
  return display.color;
};

// Get chip color for activity type
export const getActivityChipColor = (actionType) => {
  const display = getActivityDisplay(actionType);
  return display.chipColor;
};

// Check if activity type is valid
export const isValidActivityType = (actionType) => {
  return ACTIVITY_TYPES.hasOwnProperty(actionType);
};

// Get all activity types
export const getAllActivityTypes = () => {
  return Object.keys(ACTIVITY_TYPES);
};